import { useState } from 'react';
import { api } from '../../../lib/api';
import { SecretInput } from '../../ui/SecretInput';

/**
 * Troca de senha do usuário logado. Mora no modal "Minha conta", aberto pelo
 * botão Editar ao lado do nome, no canto do menu.
 *
 * Vive com estado próprio, separado do erro e do sucesso do formulário de
 * configurações: são dois fluxos que falham por motivos diferentes e não podem
 * disputar a mesma faixa de aviso.
 */
export function AccountTab() {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [salvando, setSalvando] = useState(false);

  const alterar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro('');
    setSucesso('');

    if (form.next.length < 8) {
      setErro('Nova senha precisa ter pelo menos 8 caracteres');
      return;
    }
    if (form.next !== form.confirm) {
      setErro('Confirmação de senha não bate');
      return;
    }
    if (salvando) return;

    setSalvando(true);
    try {
      const result = await api.changePassword(form.current, form.next);
      setSucesso(`Senha alterada. ${result.revokedSessions} ${result.revokedSessions === 1 ? 'sessão encerrada' : 'sessões encerradas'}. Indo para o login…`);
      setForm({ current: '', next: '', confirm: '' });
      // O backend já invalidou o cookie; o redirecionamento é só para o
      // usuário não ficar olhando uma tela que não responde mais.
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Erro ao alterar senha');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <section aria-labelledby="conta-senha-titulo">
      <h4 id="conta-senha-titulo" className="ds-micro mb-2">Senha</h4>
      <p className="text-sm mb-4" style={{ color: 'var(--text-dim)' }}>
        Ao trocar, as sessões abertas em outros aparelhos são encerradas.
      </p>

      {erro && (
        <div
          role="alert"
          aria-live="assertive"
          className="rounded-lg p-3 text-sm mb-3"
          style={{ background: 'var(--danger-soft)', border: '1px solid rgba(248,113,113,0.24)', color: 'var(--danger)' }}
        >
          {erro}
        </div>
      )}
      {sucesso && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-lg p-3 text-sm mb-3"
          style={{ background: 'var(--success-soft)', border: '1px solid rgba(74,222,128,0.24)', color: 'var(--success)' }}
        >
          {sucesso}
        </div>
      )}

      <form onSubmit={alterar} className="space-y-3">
        <div>
          <label className="ds-label" htmlFor="pw-current">Senha atual</label>
          <SecretInput
            id="pw-current"
            autoComplete="current-password"
            value={form.current}
            onChange={(valor) => setForm({ ...form, current: valor })}
            required
          />
        </div>
        <div>
          <label className="ds-label" htmlFor="pw-new">Nova senha</label>
          <SecretInput
            id="pw-new"
            autoComplete="new-password"
            value={form.next}
            onChange={(valor) => setForm({ ...form, next: valor })}
            minLength={8}
            required
            aria-describedby="pw-new-hint"
          />
          <p id="pw-new-hint" className="text-xs mt-1" style={{ color: 'var(--text-dim)' }}>
            Mínimo 8 caracteres.
          </p>
        </div>
        <div>
          <label className="ds-label" htmlFor="pw-confirm">Confirmar nova senha</label>
          <SecretInput
            id="pw-confirm"
            autoComplete="new-password"
            value={form.confirm}
            onChange={(valor) => setForm({ ...form, confirm: valor })}
            minLength={8}
            required
          />
        </div>
        <button
          type="submit"
          disabled={salvando}
          className="ds-btn ds-btn-primary"
          style={{ padding: '11px 18px' }}
        >
          {salvando ? 'Alterando…' : 'Alterar senha'}
        </button>
      </form>
    </section>
  );
}
